import { useCallback, useEffect, useState, useMemo } from 'react';
import _ from 'lodash';
import masterSlideLoader from '../utilities/masterSlideLoader';
import _dialogLoader from '../utilities/dialogLoader';
import {
  ApplyConfirmDialog,
  ApplyCancelConfirmDialog,
  DuplicateApprovedDialog,
  DuplicateWarnDialog,
  FormCloseConfirmDialog,
  RemoveConfirmDialog,
} from './AgencyRegistrationApplicationDialog';
import { UserProfileService } from '../services/UserProfiles';
import LocalStorageService from '../services/utilities/LocalStorageService';
import AgencyRegistrationApplicationService from '../services/AgencyRegistrationApplications';
import KokuzeiCompaniesService from '../services/KokuzeiCompanies';
import {
  AGENCY_REGISTRATION_APPLICATION_PAGE,
  AGENCY_REGISTRATION_APPLICATION_STATUS,
  FILE_CODE,
  STATUS_CODE,
} from '../utilities/constants';
import { onceAsyncRequest } from '../utilities/commonUtil';
import { ErrorDialog } from './dialogs/ErrorDialog';
import { messageUtility } from '../utilities/messageUtility';
import { validateByJoi } from '../utilities/validation';
import {
  agencyRegistrationApplicationSchema,
  agencyRegistrationSaveSchema,
} from '../utilities/validationSchemas/agencyRegistrationApplication';
import { PrintService } from '../services/Prints';
import PrintUtility from '../utilities/printUtility';
import {
  getFileExtension,
  approvalFileType,
  fileSizeCheck,
} from '../utilities/fileType';
import { isNullOrUndefinedOrEmptyStr } from '../utilities/stringUtil';

const initialFormValues = {
  id: '',
  company_agent_to: undefined, // 数値 代行先会社ID
  company_agent_to_name: '', // 数値 代行先会社ID
  user_agency_registration_permissions: [], //  数値の配列 （代行元）担当者ユーザーID
  original_filename: undefined, //  (アップロードファイル)覚書テンプレートファイル名
  memorandum_file: undefined, //  (アップロードファイル)覚書テンプレートファイル
  status: null, //  数値 0:未申請 1:申請
  other_agencies_count: null, //  数値
};

export const FORM_MODE = {
  REGISTER: 1,
  DETAIL: 2,
  EDIT: 3,
  ORIGIN_DETAIL: 4,
};

const approvalFileTypes = ['pdf'];

export const useAgencyRegistrationApplicationForm = (
  formMode,
  updateId = null,
  parentId = null
) => {
  // リロードすると文字列のidや'0'が流れてくるので対策
  // 呼び元の実装に影響されたくないのでここで変換
  const _updateId = useMemo(() => _.toInteger(updateId) || null, [updateId]);
  const _parentId = useMemo(() => _.toInteger(parentId) || null, [parentId]);

  return useAgencyRegistrationApplicationFormLogic(
    formMode,
    _updateId,
    _parentId
  );
};

const getUserIds = users => {
  return _(users)
    .filter(user => !_.isNil(user.id))
    .map(user => user.id)
    .uniq()
    .value();
};

const getFile = async (id, fileName) => {
  const response = await AgencyRegistrationApplicationService.downloadFile(id);
  if (!response.result) {
    return { result: response.result };
  }

  // ファイルはwordかpdfのみ
  const ext = getFileExtension(fileName);
  if (ext !== 'pdf' && ext !== 'docx') {
    return null;
  }

  const contentType =
    ext === 'pdf'
      ? 'application/pdf'
      : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  const blob = PrintUtility.convertBase64ToBlob(
    response.data.entities.data,
    contentType
  );
  blob.lastModifiedDate = new Date();
  blob.name = fileName;
  return {
    result: response.result,
    blob,
    head_pdf_url: response.data.entities.pdf_urls.head_pdf_url,
    get_pdf_url: response.data.entities.pdf_urls.get_pdf_url,
  };
};

/**
 * @description 代行登録申請のフォームロジック
 * @param {int} formMode 登録、詳細、編集か
 * @param {int} updateId 更新対象のid
 * @param {int} parentId 再申請の場合の親id 登録で再申請の場合のみ設定
 */
const useAgencyRegistrationApplicationFormLogic = (
  formMode,
  updateId = null,
  parentId = null
) => {
  const [isLoaded, setLoaded] = useState(false);

  const [formValues, setFormValues] = useState(initialFormValues);

  const [errorMessages, setErrorMessages] = useState({});

  // 代行担当者は代行担当者リストの読込APIと代行登録申請のAPIのレスポンスの型が違うので代行担当者リストに合わせる
  const [agencyUserCandidates, setAgencyUserCandidates] = useState([]);

  // 国税庁法人登録情報を取得してBuildee登録情報と差異がある場合、差異があることを表示させる
  const [isDifferent, setIsDifferent] = useState([]);

  // ボタンの制御用
  const [canEdit, setCanEdit] = useState(false);
  const [canApply, setCanApply] = useState(false);
  const [disabledApply, setDisabledApply] = useState(false);
  const [canTemporarySave, setCanTemporarySave] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [canRemove, setCanRemove] = useState(false);
  const [canApplyCancel, setCanApplyCancel] = useState(false);
  const [canReapply, setCanReapply] = useState(false);
  const [canUpdate, setCanUpdate] = useState(false);

  // 協力会社新規登録を表示するか
  const [
    isPartnerCompanyRegisterVisible,
    setIsPartnerCompanyRegisterVisible,
  ] = useState(false);

  const [canEditCompanyAgentTo, setCanEditCompanyAgentTo] = useState(true);
  const [canEditMemorandumFile, setCanEditMemorandumFile] = useState(true);
  const [canDownloadMemorandumFile, setCanDownloadMemorandumFile] = useState(
    true
  );
  const [selectedPartnerCompanyData, setSelectedPartnerCompanyData] = useState(
    undefined
  );
  const [
    isPartnerCompanyAddDialogOpen,
    setIsPartnerCompanyAddDialogOpen,
  ] = useState(false);
  const [
    partnerCompanyAddDialogSearchParam,
    setPartnerCompanyAddDialogSearchParam,
  ] = useState({});
  this.isCheckingDuplicate = false;

  const moveListPage = () => {
    switch (formMode) {
      case FORM_MODE.REGISTER:
      case FORM_MODE.EDIT:
      case FORM_MODE.DETAIL: {
        masterSlideLoader.show(
          AGENCY_REGISTRATION_APPLICATION_PAGE.LIST.COMPONENT_NAME,
          {
            state: {},
          }
        );
        break;
      }
      case FORM_MODE.ORIGIN_DETAIL: {
        masterSlideLoader.show(
          AGENCY_REGISTRATION_APPLICATION_PAGE.ORIGINS.COMPONENT_NAME,
          {
            state: {},
          }
        );
        break;
      }
      default:
        break;
    }
  };

  const moveDetailPage = id => {
    const toId = _.isString(id) || _.isInteger(id) ? id : updateId;
    switch (formMode) {
      case FORM_MODE.REGISTER:
      case FORM_MODE.EDIT:
      case FORM_MODE.DETAIL: {
        masterSlideLoader.show(
          AGENCY_REGISTRATION_APPLICATION_PAGE.DETAIL.COMPONENT_NAME,
          {
            state: { id: toId },
          }
        );
        break;
      }
      case FORM_MODE.ORIGIN_DETAIL: {
        masterSlideLoader.show(
          AGENCY_REGISTRATION_APPLICATION_PAGE.ORIGIN_DETAIL.COMPONENT_NAME,
          {
            state: { id: toId },
          }
        );
        break;
      }
      default:
        break;
    }
  };

  const moveEditPage = id => {
    const toId = _.isString(id) || _.isInteger(id) ? id : updateId;
    masterSlideLoader.show(
      AGENCY_REGISTRATION_APPLICATION_PAGE.EDIT.COMPONENT_NAME,
      {
        state: { id: toId },
      }
    );
  };

  const formClose = () => {
    if (formMode === FORM_MODE.EDIT) {
      moveDetailPage();
    } else {
      moveListPage();
    }
  };

  const setRegisterActionsEnable = () => {
    setCanApply(true);
    setCanTemporarySave(true);
  };

  const setDetailActionsEnable = statusNo => {
    switch (statusNo) {
      case AGENCY_REGISTRATION_APPLICATION_STATUS.NOT_APPLIED.ID:
        setCanEdit(true);
        setCanApply(true);
        setCanRemove(true);
        break;
      case AGENCY_REGISTRATION_APPLICATION_STATUS.APPLIED.ID:
        setCanApplyCancel(true);
        break;
      case AGENCY_REGISTRATION_APPLICATION_STATUS.APPROVED.ID:
        break;
      case AGENCY_REGISTRATION_APPLICATION_STATUS.REJECTED.ID:
      case AGENCY_REGISTRATION_APPLICATION_STATUS.EXPIRED.ID:
        setCanReapply(true);
        break;
      default:
        break;
    }
  };

  const setOriginDetailActionsEnable = statusNo => {
    if (statusNo === AGENCY_REGISTRATION_APPLICATION_STATUS.APPROVED.ID) {
      setCanDownloadMemorandumFile(true);
    } else {
      setCanDownloadMemorandumFile(false);
    }
  };

  const setEditActionsEnable = statusNo => {
    switch (statusNo) {
      case AGENCY_REGISTRATION_APPLICATION_STATUS.NOT_APPLIED.ID:
        setCanApply(true);
        setCanTemporarySave(true);
        setCanRemove(true);
        break;
      case AGENCY_REGISTRATION_APPLICATION_STATUS.APPROVED.ID:
        setCanUpdate(true);
        setCanEditCompanyAgentTo(false);
        setCanEditMemorandumFile(false);
        break;
      default:
        break;
    }
  };

  const onSelectUserCandidate = (index, newUser) => {
    setAgencyUserCandidates(prevAgencyUserCandidates => {
      return _.map(prevAgencyUserCandidates, (user, i) => {
        if (i === index) {
          // 選択を外すとundefinedでくる
          return newUser || {};
        } else {
          return user;
        }
      });
    });
  };

  const onAddUserCandidate = () => {
    setAgencyUserCandidates(prevAgencyUserCandidates => {
      return [...prevAgencyUserCandidates, {}];
    });
  };

  const onDeleteUserCandidate = index => {
    setAgencyUserCandidates(prevAgencyUserCandidates => {
      return _.filter(prevAgencyUserCandidates, (__, i) => i !== index);
    });
  };

  const onSelectCompany = async company => {
    setSelectedPartnerCompanyData(undefined);
    const isSuccess = await checkDuplicateError(company.id);
    if (isSuccess) {
      const kokuzeiCompanyInfo = {
        name: null,
        company_number: null,
        address: null,
      };
      if (!_.isEmpty(company.company_number)) {
        // [A-878-2_2] 法人の場合、国税庁法人登録情報も取得
        const kokuzeiCompanies = await getKokuzeiCompanies({
          company_number: company.company_number,
        });
        if (!_.isEmpty(kokuzeiCompanies)) {
          kokuzeiCompanyInfo.name = kokuzeiCompanies[0]['name'];
          kokuzeiCompanyInfo.company_number =
            kokuzeiCompanies[0]['company_number'];
          kokuzeiCompanyInfo.address =
            kokuzeiCompanies[0]['prefecture_name'] +
            kokuzeiCompanies[0]['city_name'] +
            kokuzeiCompanies[0]['street_number'];
          const differentData = ['name', 'address'].reduce((result, value) => {
            if (value === 'address') {
              let company_address = '';
              if (company.company) {
                company_address = company.company.address;
              } else {
                company_address = `${company.prefecture_name ||
                  ''}${company.city_name || ''}${company.street_number ||
                  ''}${company.building_name || ''}`;
                company_address = company_address.trim();
              }
              if (!_.isEqual(kokuzeiCompanyInfo[value], company_address)) {
                result.push(value);
              }
            } else {
              if (!_.isEqual(kokuzeiCompanyInfo[value], company[value])) {
                result.push(value);
              }
            }
            return result;
          }, []);
          setIsDifferent(differentData);
        }
      } else {
        setIsDifferent([]);
      }
      const otherAgenciesCount = await getOtherAgenciesCount(company.id);
      let company_address = '';
      if (company.company) {
        company_address = company.company.address;
      } else {
        company_address = `${company.prefecture_name ||
          ''}${company.city_name || ''}${company.street_number ||
          ''}${company.building_name || ''}`;
      }
      setFormValues(prev => ({
        ...prev,
        company_agent_to: company.id,
        company_agent_to_name: company.name,
        company_agent_to_address: company_address,
        company_agent_to_company_number: company.company_number, // 個人事業主の場合はnull
        company_agent_to_postal_code: company.postal_code,
        company_agent_to_tel: company.tel,
        company_agent_to_one_master_flag: company.one_master_flag,
        company_agent_to_one_owner_flag: company.one_owner_flag,
        other_agencies_count: otherAgenciesCount,
        kokuzei_name: kokuzeiCompanyInfo.name,
        kokuzei_company_number: kokuzeiCompanyInfo.company_number,
        kokuzei_address: kokuzeiCompanyInfo.address,
      }));
      // 会社が変更された場合はファイルが会社と結びつかなくなるので覚書ファイルを削除する
      if (formMode === FORM_MODE.EDIT) {
        onDeleteFile();
      }
    }
  };

  // 代行登録申請重複チェック
  const checkDuplicateError = async companyAgentTo => {
    if (this.isCheckingDuplicate) {
      return;
    }
    this.isCheckingDuplicate = true;
    const response = await AgencyRegistrationApplicationService.checkDuplicate(
      companyAgentTo
    );
    if (!response.result) {
      this.isCheckingDuplicate = false;
      return false;
    }

    if (
      !_.isEmpty(
        _.get(response.data, 'entities.agency_registration_application')
      )
    ) {
      const data = response.data.entities.agency_registration_application;
      const toId = data.id;
      // 編集だと同じ場合もあるので、同じなら何もしない
      if (toId !== updateId) {
        if (
          data.status_no === AGENCY_REGISTRATION_APPLICATION_STATUS.APPROVED.ID
        ) {
          // 代行先が既に承認済みの場合
          _dialogLoader.show(DuplicateApprovedDialog, {
            moveDuplicateApplyDetail: () => {
              _dialogLoader.close();
              masterSlideLoader.show('AgencyCompanies', {
                state: {},
              });
            },
          });
        } else {
          _dialogLoader.show(DuplicateWarnDialog, {
            moveDuplicateApplyDetail: () => {
              _dialogLoader.close();
              if (
                // 代行先が申請待の場合、申請待の編集画面に移動
                data.status_no ===
                AGENCY_REGISTRATION_APPLICATION_STATUS.NOT_APPLIED.ID
              ) {
                moveEditPage(toId);
              } else {
                // 代行先が申請中の場合、詳細画面に移動
                moveDetailPage(toId);
              }
            },
          });
        }
        this.isCheckingDuplicate = false;
        return false;
      }
    }
    this.isCheckingDuplicate = false;
    return true;
  };

  const getKokuzeiCompanies = async params => {
    const response = await KokuzeiCompaniesService.list(params);
    if (response.result) {
      return response.data.entities.kokuzei_companies;
    }
  };

  // 自社以外の代行実施協力会社セット
  const getOtherAgenciesCount = async companyAgentTo => {
    const response = await AgencyRegistrationApplicationService.otherAgenciesCount(
      companyAgentTo
    );
    if (response.result) {
      return response.data.entities.other_agencies_count;
    } else {
      0;
    }
  };

  const downloadFile = useCallback(
    onceAsyncRequest(async () => {
      const ext = getFileExtension(formValues.original_filename);
      const isPdf = ext === 'pdf';
      //pdfの場合は新規タブを開く
      const newTab = isPdf ? window.open('/LayoutPrint', '_blank') : null;
      let needCloseTab = isPdf ? true : false;
      try {
        const { blob, head_pdf_url, get_pdf_url } = await getFile(
          updateId,
          formValues.original_filename
        );
        if (blob === null) {
          return;
        }

        PrintUtility.printBlob(
          blob,
          formValues.original_filename,
          isPdf ? FILE_CODE.PDF : FILE_CODE.WORD
        );

        if (!isPdf) {
          return;
        }

        const responseS3AWS = await PrintService.getHeaderReponseURL(
          head_pdf_url
        );
        if (responseS3AWS.status !== STATUS_CODE.ok) {
          return;
        }

        newTab.location.href = get_pdf_url;
        needCloseTab = false;
      } finally {
        if (needCloseTab) {
          newTab.close();
        }
      }
    }),
    [updateId, formValues.original_filename]
  );

  const onSetFile = file => {
    if (!_.isObject(file)) {
      return;
    }
    setFormValues(prev => ({
      ...prev,
      original_filename: file.name,
      memorandum_file: file,
    }));
  };

  const onDeleteFile = () => {
    setFormValues(prev => ({
      ...prev,
      original_filename: undefined,
      memorandum_file: undefined,
    }));
  };

  // 多重実行防止(onceAsyncRequest)のために一意な関数にする
  // 外部の値には依存しない 必要なら引数に追加
  const execCreate = useCallback(
    onceAsyncRequest(
      async (
        {
          company_agent_to,
          company_agent_to_users,
          memorandum_file,
          status,
          parent_id,
          original_filename,
        },
        onSuccess
      ) => {
        // 再申請の場合でファイルを変更していない場合はファイルを取得する
        let newFile = memorandum_file;
        if (_.isNil(memorandum_file)) {
          const { blob } = await getFile(parent_id, original_filename);
          newFile = blob;
        }
        if (_.isNil(newFile)) {
          _dialogLoader.show(ErrorDialog, {
            message: messageUtility.getMessage('E-000-006'),
            callback: {
              close: () => {
                _dialogLoader.close();
              },
            },
          });
          return;
        }

        const response = await AgencyRegistrationApplicationService.create({
          company_agent_to,
          company_agent_to_users,
          memorandum_file: newFile,
          status,
          parent_id,
        });
        if (response.result) {
          onSuccess();
        } else {
          _dialogLoader.show(ErrorDialog, {
            message: response.message || messageUtility.getMessage('E-000-006'),
            callback: {
              close: () => {
                _dialogLoader.close();
              },
            },
          });
        }
      }
    ),
    []
  );

  // 多重実行防止(onceAsyncRequest)のために一意な関数にする
  // 外部の値には依存しない 必要なら引数に追加
  const execSave = useCallback(
    onceAsyncRequest(
      async (
        {
          company_agent_to,
          company_agent_to_users,
          memorandum_file,
          status,
          parent_id,
          original_filename,
          file_deleted_flag,
        },
        onSuccess
      ) => {
        setIsSending(true);
        // 再申請の場合でファイルを変更していない場合はファイルを取得する
        let newFile = memorandum_file;
        if (_.isNil(memorandum_file) && !_.isNil(original_filename)) {
          const { blob } = await getFile(parent_id, original_filename);
          newFile = blob;
        }

        const paramSave = {
          company_agent_to,
          company_agent_to_users,
          status,
          parent_id,
        };

        /**
         * If the new file exists or the file is not marked as deleted,
         * set the `memorandum_file` property of the `paramSave` object to the new file.
         */
        if (newFile || !file_deleted_flag) {
          paramSave.memorandum_file = newFile;
        }

        const response = await AgencyRegistrationApplicationService.create(
          paramSave
        );
        if (response.result) {
          onSuccess();
          setIsSending(false);
        } else {
          _dialogLoader.show(ErrorDialog, {
            message: response.message || messageUtility.getMessage('E-000-006'),
            callback: {
              close: () => {
                _dialogLoader.close();
                setIsSending(false);
              },
            },
          });
        }
      }
    ),
    []
  );

  const execUpdate = useCallback(
    onceAsyncRequest(
      async (
        id,
        {
          company_agent_to,
          company_agent_to_users,
          memorandum_file,
          status,
          parent_id,
          original_filename,
          file_deleted_flag,
        },
        onSuccess
      ) => {
        setIsSending(true);
        let newFile = memorandum_file;
        if (_.isNil(memorandum_file) && _.isNil(original_filename)) {
          // 編集画面で「覚書の添付」が削除された場合、サーバー側の添付ファイルを削除するように''に設定する
          newFile = '';
        }

        const paramSave = {
          company_agent_to,
          company_agent_to_users,
          status,
          parent_id,
        };
        /**
         * If the new file exists or the file is not marked as deleted,
         * set the `memorandum_file` property of the `paramSave` object to the new file.
         */
        if (newFile || !file_deleted_flag) {
          paramSave.memorandum_file = newFile;
        }
        const response = await AgencyRegistrationApplicationService.update(
          id,
          paramSave
        );
        if (response.result) {
          onSuccess();
          setIsSending(false);
        } else {
          _dialogLoader.show(ErrorDialog, {
            message: response.message || messageUtility.getMessage('E-000-005'),
            callback: {
              close: () => {
                _dialogLoader.close();
                setIsSending(false);
              },
            },
          });
        }
      }
    ),
    []
  );

  const save = async status => {
    const params = {
      company_agent_to: formValues.company_agent_to,
      company_agent_to_users: getUserIds(agencyUserCandidates),
      memorandum_file: formValues.memorandum_file,
      original_filename: formValues.original_filename,
      status,
      parent_id: parentId,
      tel: formValues.tel,
      file_deleted_flag: formValues.file_deleted_flag,
    };
    if (await validateSave(params)) {
      if (formMode === FORM_MODE.REGISTER) {
        const isSuccess = await checkDuplicateError(
          formValues.company_agent_to
        );
        if (isSuccess) {
          execSave(params, formClose);
        }
      } else {
        execUpdate(formValues.id, params, formClose);
      }
    }
  };

  const validateAndSetMessage = (name, value) => {
    const fieldSchema = {
      [name]: agencyRegistrationApplicationSchema[name],
    };

    const validateResult = validateByJoi(fieldSchema, { [name]: value });
    setErrorMessages(errorMessages => ({
      ...errorMessages,
      [name]: _.get(validateResult.messages, name, undefined),
    }));
    return validateResult.result;
  };

  const fileValidateAndSetMessage = async file => {
    let message = '';
    // 必須チェックは別なので値がある場合のみ検証する
    if (!_.isNil(file)) {
      try {
        const result = await approvalFileType(file, approvalFileTypes);
        if (!result) {
          message = messageUtility.getMessage('E-000-038', approvalFileTypes);
        } else if (!(await fileSizeCheck(file, 10485760))) {
          message = messageUtility.getMessage('E-000-046', '10');
        }
      } catch (e) {
        // iPadのみファイル添付から時間が経つと正常なファイルでも添付が失敗する
        message = 'ファイルを再添付してください。';
      }
    }
    setErrorMessages(prev => ({
      ...prev,
      memorandum_file: message,
    }));

    return message === '';
  };

  const validateAll = async ({
    company_agent_to,
    company_agent_to_users,
    memorandum_file,
    original_filename,
  }) => {
    const validateResult = validateByJoi(agencyRegistrationApplicationSchema, {
      company_agent_to,
      company_agent_to_users,
      original_filename,
    });
    setErrorMessages(prev => ({ ...prev, ...validateResult.messages }));
    const fileCheckResult = await fileValidateAndSetMessage(memorandum_file);
    return validateResult.result && fileCheckResult;
  };

  const validateSave = async ({
    company_agent_to,
    company_agent_to_users,
    memorandum_file,
    original_filename,
  }) => {
    const validateResult = validateByJoi(agencyRegistrationSaveSchema, {
      company_agent_to,
      company_agent_to_users,
      original_filename,
    });
    setErrorMessages(prev => ({ ...prev, ...validateResult.messages }));
    const fileCheckResult = await fileValidateAndSetMessage(memorandum_file);
    return validateResult.result && fileCheckResult;
  };

  // 一時保存
  const handleTemporarySave = () => {
    if (isSending) {
      return;
    } else {
      save(AGENCY_REGISTRATION_APPLICATION_STATUS.NOT_APPLIED.ID);
    }
  };

  // 申請
  const handleApply = async () => {
    if (disabledApply) {
      return;
    } else {
      const params = {
        company_agent_to: formValues.company_agent_to,
        company_agent_to_users: getUserIds(agencyUserCandidates),
        memorandum_file: formValues.memorandum_file,
        original_filename:
          formValues.original_filename === ''
            ? undefined
            : formValues.original_filename,
        parent_id: parentId,
      };
      const validateResult = await validateAll(params);
      if (validateResult) {
        _dialogLoader.show(ApplyConfirmDialog, {
          apply: async () => {
            const isSuccess = await checkDuplicateError(
              formValues.company_agent_to
            );
            if (isSuccess) {
              save(AGENCY_REGISTRATION_APPLICATION_STATUS.APPLIED.ID);
              _dialogLoader.close();
            }
          },
        });
      }
    }
  };

  const execRemove = useCallback(
    onceAsyncRequest(async (id, onSuccess) => {
      const response = await AgencyRegistrationApplicationService.del(id);
      if (response.result) {
        onSuccess();
      } else {
        _dialogLoader.show(ErrorDialog, {
          message: response.message || messageUtility.getMessage('E-000-005'),
          callback: {
            close: () => {
              _dialogLoader.close();
            },
          },
        });
      }
    }),
    []
  );

  // 削除
  const handleRemove = () => {
    _dialogLoader.show(RemoveConfirmDialog, {
      remove: () => {
        execRemove(formValues.id, moveListPage);
        _dialogLoader.close();
      },
    });
  };

  // 申請取り消し
  const handleApplyCancel = () => {
    _dialogLoader.show(ApplyCancelConfirmDialog, {
      applyCancel: () => {
        save(AGENCY_REGISTRATION_APPLICATION_STATUS.NOT_APPLIED.ID);
        _dialogLoader.close();
      },
    });
  };

  // 再申請
  const handleReapply = async () => {
    const isSuccess = await checkDuplicateError(formValues.company_agent_to);
    if (isSuccess) {
      // 登録画面に遷移して現在のidを元に登録する
      masterSlideLoader.show(
        AGENCY_REGISTRATION_APPLICATION_PAGE.REGISTER.COMPONENT_NAME,
        {
          state: { id: formValues.id },
        }
      );
    }
  };

  // 更新
  const handleUpdate = () => {
    save(formValues.status_no);
  };

  const movePartnerCompanyRegister = () => {
    setIsPartnerCompanyRegisterVisible(true);
  };

  const closePartnerCompanyRegister = cacheParam => {
    if (cacheParam) {
      setIsPartnerCompanyRegisterVisible(false);
      setIsPartnerCompanyAddDialogOpen(true);
      setPartnerCompanyAddDialogSearchParam(cacheParam);
    } else {
      setIsPartnerCompanyRegisterVisible(false);
      setSelectedPartnerCompanyData(undefined);
      setPartnerCompanyAddDialogSearchParam({});
    }
  };

  const handleFormClose = () => {
    switch (formMode) {
      case FORM_MODE.REGISTER:
      case FORM_MODE.EDIT: {
        _dialogLoader.show(FormCloseConfirmDialog, {
          formClose: () => {
            _dialogLoader.close();
            formClose();
          },
        });
        break;
      }
      case FORM_MODE.DETAIL:
      case FORM_MODE.ORIGIN_DETAIL: {
        formClose();
        break;
      }
      default:
        break;
    }
  };

  useEffect(
    () => {
      setLoaded(false);
      setFormValues(initialFormValues);
      setErrorMessages({});
      setAgencyUserCandidates([]);
      setCanEdit(false);
      setCanApply(false);
      setCanTemporarySave(false);
      setCanRemove(false);
      setCanApplyCancel(false);
      setCanReapply(false);
      setIsPartnerCompanyRegisterVisible(false);
      setCanEditCompanyAgentTo(true);
      setCanEditMemorandumFile(true);
      setCanDownloadMemorandumFile(true);

      const isReapply = !_.isNull(parentId);
      if (formMode === FORM_MODE.REGISTER && !isReapply) {
        // 通常の新規登録の場合
        (async () => {
          // 代行担当者に自ユーザを追加
          const response = await UserProfileService.show(
            LocalStorageService.getUserId(),
            {}
          );
          if (response.result) {
            const userProfile = response.data.entities.user_profile;
            setAgencyUserCandidates([
              {
                id: userProfile.id,
                display_user_fullname: `${userProfile.name_sei} ${
                  userProfile.name_mei
                }`,
              },
            ]);
          }
          setRegisterActionsEnable();
          setLoaded(true);
        })();
      } else {
        // 再申請、詳細、編集は読込実行
        (async () => {
          if (_.isNull(updateId) && _.isNull(parentId)) {
            //不正な遷移の場合は一覧に移動
            moveListPage();
            return;
          }
          const response = await AgencyRegistrationApplicationService.show(
            updateId || parentId
          );
          if (response.result) {
            const data = response.data.entities.agency_registration_application;
            // 国税庁法人登録情報取得
            const kokuzeiCompanyInfo = {
              name: null,
              company_number: null,
              address: null,
            };
            const companyNumber = data.company_agent_to_company_number;
            if (!isNullOrUndefinedOrEmptyStr(companyNumber)) {
              const kokuzeiCompanies = await getKokuzeiCompanies({
                company_number: companyNumber,
              });
              if (!_.isEmpty(kokuzeiCompanies)) {
                kokuzeiCompanyInfo.name = kokuzeiCompanies[0]['name'];
                kokuzeiCompanyInfo.company_number =
                  kokuzeiCompanies[0]['company_number'];
                kokuzeiCompanyInfo.address =
                  kokuzeiCompanies[0]['prefecture_name'] +
                  kokuzeiCompanies[0]['city_name'] +
                  kokuzeiCompanies[0]['street_number'];
                const differentData = ['name', 'address'].reduce(
                  (result, value) => {
                    if (
                      !_.isEqual(
                        kokuzeiCompanyInfo[value],
                        data[`company_agent_to_${value}`]
                      )
                    ) {
                      result.push(value);
                    }
                    return result;
                  },
                  []
                );
                setIsDifferent(differentData);
              }
            } else {
              setIsDifferent([]);
            }
            // 失効ステータスの申請データはoriginal_filenameに空文字が設定されてるため、
            // バリデーションチェックに引っかかるように、undefinedで上書きする。
            let originalFileName = data.original_filename;
            if (
              originalFileName === '' &&
              formMode === FORM_MODE.REGISTER &&
              data.status_no ===
                AGENCY_REGISTRATION_APPLICATION_STATUS.EXPIRED.ID
            ) {
              originalFileName = undefined;
            }

            /**
             * If the file is marked as deleted and the form mode is not DETAIL or ORIGIN_DETAIL,
             * set the originalFileName to undefined.
             */
            if (data.file_deleted_flag && !_.includes([FORM_MODE.DETAIL, FORM_MODE.ORIGIN_DETAIL], formMode)) {
              originalFileName = undefined;
            }

            setFormValues({
              ...data,
              original_filename: originalFileName,
              related_applications: response.data.entities.related_applications,
              kokuzei_name: kokuzeiCompanyInfo.name,
              kokuzei_company_number: kokuzeiCompanyInfo.company_number,
              kokuzei_address: kokuzeiCompanyInfo.address,
            });

            setAgencyUserCandidates(
              _.map(data.user_agency_registration_permissions, v => {
                return {
                  id: v.user_id,
                  display_user_fullname: v.display_user_fullname,
                };
              })
            );

            // 一時保存などで詳細画面で「代行担当者」「覚書の添付」が無い場合、申請ボタンを押下不可にする
            // i.e. 申請バリデーションで引っかかる場合
            if (formMode === FORM_MODE.DETAIL) {
              const validateResult = validateByJoi(
                agencyRegistrationApplicationSchema,
                {
                  company_agent_to: data.company_agent_to,
                  company_agent_to_users:
                    data.user_agency_registration_permissions,
                  original_filename:
                    originalFileName === '' ? undefined : originalFileName,
                }
              );
              if (!validateResult.result || data.file_deleted_flag) {
                setDisabledApply(true);
              }
            }

            switch (formMode) {
              case FORM_MODE.REGISTER: {
                setRegisterActionsEnable();
                break;
              }
              case FORM_MODE.EDIT: {
                setEditActionsEnable(data.status_no);
                break;
              }
              case FORM_MODE.DETAIL: {
                setDetailActionsEnable(data.status_no);
                break;
              }
              case FORM_MODE.ORIGIN_DETAIL: {
                setOriginDetailActionsEnable(data.status_no);
                break;
              }
              default:
                break;
            }
          }
          setLoaded(true);
        })();
      }
    },
    // 関連する申請から詳細から詳細に遷移する場合にupdateIdが変わるので注意
    // moveListPageを呼んでいるがformModeにしか依存していない、依存するstateが増えたとしてもuseEffectが実行された時点の状態なら問題ない
    [formMode, updateId, parentId]
  );

  useEffect(
    () => {
      if (isLoaded) {
        validateAndSetMessage('company_agent_to', formValues.company_agent_to);
      }
    },
    // 初回をスキップしたいのでisLoadedは含めない
    // validateAndSetMessageは関数内でstateを参照していないので含めなくても問題ない
    [formValues.company_agent_to]
  );

  useEffect(
    () => {
      if (isLoaded) {
        validateAndSetMessage(
          'original_filename',
          formValues.original_filename
        );
        fileValidateAndSetMessage(formValues.memorandum_file);
      }
    },
    // 初回をスキップしたいのでisLoadedは含めない
    // fileValidateAndSetMessageは関数内でstateを参照していないので含めなくても問題ない
    [formValues.original_filename, formValues.memorandum_file]
  );

  useEffect(
    () => {
      if (isLoaded) {
        validateAndSetMessage('company_agent_to', formValues.company_agent_to);
      }
    },
    // 初回をスキップしたいのでisLoadedは含めない
    // fileValidateAndSetMessageは関数内でstateを参照していないので含めなくても問題ない
    [formValues.company_agent_to]
  );

  return {
    formValues,
    errorMessages,
    downloadFile,
    agencyUserCandidates,
    isDifferent,
    onSelectUserCandidate,
    onAddUserCandidate,
    onDeleteUserCandidate,
    onSelectCompany,
    onSetFile,
    onDeleteFile,
    canEdit,
    canApply,
    handleApply,
    disabledApply,
    setDisabledApply,
    canTemporarySave,
    handleTemporarySave,
    canRemove,
    handleRemove,
    canApplyCancel,
    handleApplyCancel,
    canReapply,
    handleReapply,
    canUpdate,
    handleUpdate,
    moveDetailPage,
    moveEditPage,
    handleFormClose,
    movePartnerCompanyRegister,
    closePartnerCompanyRegister,
    isPartnerCompanyRegisterVisible,
    canEditCompanyAgentTo,
    canEditMemorandumFile,
    canDownloadMemorandumFile,
    selectedPartnerCompanyData,
    setSelectedPartnerCompanyData,
    isPartnerCompanyAddDialogOpen,
    setIsPartnerCompanyAddDialogOpen,
    partnerCompanyAddDialogSearchParam,
    setPartnerCompanyAddDialogSearchParam,
  };
};



// WEBPACK FOOTER //
// ./src/components/useAgencyRegistrationApplicationForm.js